import type { Role } from '@ops/contracts'

import type { IconName } from '../ui/Icon'

/**
 * La navegación se deriva del rol para no ofrecer pantallas que la API va a
 * rechazar con 403. **El servidor sigue siendo la autoridad**: esconder un link
 * es cortesía, no seguridad.
 *
 * Los iconos son del set propio en SVG, no emojis: un emoji cambia de forma en
 * cada sistema operativo y un lector de pantalla lo lee como palabra.
 */
export interface NavItem {
  to: string
  label: string
  icon: IconName
  roles: readonly Role[]
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Overview', icon: 'panel', roles: ['admin', 'ops', 'comercial'] },
  { to: '/revenue', label: 'Revenue', icon: 'plata', roles: ['admin'] },
  {
    to: '/clientes',
    label: 'Clientes',
    icon: 'clientes',
    roles: ['admin', 'ops', 'comercial'],
  },
  /**
   * `/usage` lo ven los tres roles **aunque tenga dólares adentro**, a
   * diferencia de Revenue. La excepción es deliberada y está acotada por campo,
   * no por ruta: el costo de servir habilita decisiones de eficiencia y viaja
   * para todos; lo que paga el cliente y el margen por cuenta los recorta el
   * servidor. La definición del corte vive al lado de `MONEY_METRIC_SLUGS` en el
   * middleware de RBAC de la API — que es la autoridad, como siempre.
   */
  {
    to: '/usage',
    label: 'Uso',
    icon: 'sube',
    roles: ['admin', 'ops', 'comercial'],
  },
  {
    to: '/adquisicion',
    label: 'Adquisición',
    icon: 'megafono',
    roles: ['admin', 'comercial'],
  },
  {
    to: '/entrevistas',
    label: 'Entrevistas',
    icon: 'conversacion',
    roles: ['admin', 'ops', 'comercial'],
  },
  { to: '/admin', label: 'Admin', icon: 'engranaje', roles: ['admin'] },
]

export function navItemsForRole(rol: Role): NavItem[] {
  return NAV_ITEMS.filter((item) => item.roles.includes(rol))
}

/** El item de nav de una ruta exacta, o `undefined` si la ruta no existe. */
export function navItemDe(path: string): NavItem | undefined {
  return NAV_ITEMS.find((item) => item.to === path)
}

/**
 * El item de nav de una **ubicación**, que puede ser la sección o el registro de
 * adentro: `/clientes/cli-armytech` sigue siendo Clientes.
 *
 * Existe porque el título del header sale de esta tabla y las fichas viven en la
 * URL. Sin esto, abrir una ficha por link directo dejaba el header diciendo
 * "Pantalla no encontrada" sobre una pantalla que sí existe.
 *
 * Solo baja **un** segmento: `/clientes/a/b` no es un registro de Clientes, es
 * una 404, y el header tiene que decirlo.
 */
export function navItemDeUbicacion(pathname: string): NavItem | undefined {
  const exacto = navItemDe(pathname)
  if (exacto !== undefined) return exacto

  return NAV_ITEMS.find((item) => {
    if (item.to === '/') return false
    if (!pathname.startsWith(`${item.to}/`)) return false
    const resto = pathname.slice(item.to.length + 1)
    return resto !== '' && !resto.includes('/')
  })
}

/**
 * Roles habilitados para una ruta. `undefined` = la ruta no está en el mapa, o
 * sea que es una 404 y no un problema de permisos.
 */
export function rolesDeRuta(path: string): readonly Role[] | undefined {
  return navItemDe(path)?.roles
}

/**
 * Si el rol puede abrir la ruta.
 *
 * Las guardas del router y el filtro de la sidebar salen de **esta misma tabla**,
 * y por eso no pueden discrepar: un link visible que lleva a "acceso denegado", o
 * una ruta abierta que no figura en la navegación, serían dos listas de permisos
 * a mano esperando desincronizarse. Sigue sin ser seguridad — la API aplica
 * `requireRole` sobre cada endpoint.
 */
export function puedeVer(rol: Role, path: string): boolean {
  const roles = rolesDeRuta(path)
  return roles !== undefined && roles.includes(rol)
}
